import {
  type LDClient,
  type LDContext,
  type LDContextCommon,
  type LDMultiKindContext,
  type LDSingleKindContext,
  withLDProvider,
} from "launchdarkly-react-client-sdk";
import type React from "react";

import { getIP } from "@/lib/ip-getter";
import { Version } from "@/lib/types/version";
import { VITE_LAUNCHDARKLY_SDK_KEY } from "@/lib/utils/config";
import { logError } from "@/lib/utils/log-error";

export interface FeatureFlags {
  "permite-atendimentos-de-ate-15-minutos": boolean;
  "duracao-maxima-do-tipo-de-atendimento-em-minutos": number;
  "projeto-preco-flexivel": boolean;
  "minimal-app-version": string;
  "antecedencia-minima-para-criacao-de-agendamento-com-preco-flexivel": number;
  "duracao-tipo-atendimento-padrao-porto": number;
  "modalidade-periodo-garantido-por-usuario": number;
  "exibir-opcao-periodo-garantido-menu-app": boolean;
  "exibe-sugestao-de-duracao-para-tipo-de-atendimento-no-app": boolean;
  "exibir-drawer-de-feedback-ao-criar-um-agendamento": boolean;
  "captura-feedback-sobre-horario-desejado": boolean;
  "exibir-indique-e-ganhe": boolean;
  "exibe-marcar-paciente-como-atendido-na-sala-de-espera": boolean;
  "atualiza-mutation-receber-sala-agora-no-app-novo": boolean;
  "configuracao-banner-csat": CsatConfig;
  "inclui-salas-flex-em-tipos-de-atendimento-que-aceitam-apenas-multi": boolean;
  "exibe-botoes-de-indicacao-de-membros-para-pacientes": boolean;
  "deve-considerar-plano-mensal-como-permanencia-de-3-meses": boolean;
  "permitir-estender-horario-de-agendamento": boolean;
  "deve-exibir-etapa-de-redes-socias-no-fluxo-do-one-liv-no-app": boolean;
  "configura-rotas-que-aparece-barrinha-de-feedback": RoutesShowUserFeedbackConfig;
  "envio-automatico-link-pagamento": boolean;
  "exibe-menu-visao-geral-relatorio-atendimento": boolean;
  "exibir-menu-livance-via": boolean;
  "exibir-relatorio-de-consulta": boolean;
  "ativa-fluxo-de-alterar-status-do-pagamento": boolean;
  "exibir-novo-componente-de-contratacao-do-livance-assist-pro": boolean;
  "exibe-banner-assist-no-menu": boolean;
  "altera-telefone-por-telefone-internacional": boolean;
  "habilita-digitacao-direta-de-data-de-nascimento": boolean;
  "habilita-item-de-relatorio-do-site-no-menu-do-assist": boolean;
  "remove-lista-de-recebimentos-de-gerenciar-pagamentos": boolean;
  "configuracao-condicoes-gerais-de-uso": GeneralTermsConfig;
  "exibe-botao-de-criar-pagamento-antecipado-no-drawer-de-agendamento-confirmado": boolean;
  "exibe-melhorias-do-relatorio-de-contatos": boolean;
  "exibe-nova-tela-de-paciente": boolean;
  "exibe-novo-relatorio-de-recuperacao-de-agendamentos": boolean;
  "exibe-campos-de-subespecialidades": boolean;
  "exibe-configuracoes-de-funcionalidades-assist": boolean;
  "exibe-termos-de-uso-link-pagamento": boolean;
  "mostra-comunidade-no-app": boolean;
  "mostra-aba-de-favoritos-na-comunidade-do-app": boolean;
  "exibe-busca-de-horarios-em-unidades-proximas": boolean;
  "exibe-botao-de-realizar-estorno": boolean;
  "exibe-filtro-no-historico-de-uso": boolean;
  "utiliza-v-5-da-pagar-me": boolean;
  "grupo-teste-ab-livance-pay-home": string;
  "grupo-teste-ab-livance-assist-home": string;
  "envio-de-pix-no-totem": boolean;
  "ativa-pull-to-refresh-na-home": boolean;
  "exibe-nova-experiencia-de-filtros": boolean;
  "membro-pode-contratar-pacote-servicos": boolean;
  "grupo-teste-ab-banner-assist-home": string;
  "exibe-central-de-notificacoes": boolean;
  "habilita-historico-de-contatos-no-menu-do-assist": boolean;
  "exibe-valor-previsto-no-historico-de-contatos": boolean;
  "valor-do-gentileza-gera-gentileza": number;
  "tempo-minimo-para-tipo-de-atendimento": number;
  "exibe-fluxo-de-reexecutar-cobranca": boolean;
  "exibe-acoes-promocionais-do-membro-no-perfil-do-app": boolean;
  "textos-rota-info-home": TextsHomeInfoItemIdRoute;
  "textos-confirmacao-rota-info-home": TextsConfirmationInfoItemIdRoute;
  "gerenciar-titulo-e-descricao-do-gentileza-gera-gentileza": InfoVariantBannerReferral;
  "exibir-banner-de-gentileza-gera-gentileza": RoutesShowBannerReferralConfig;
  "ativa-reexecucao-de-cobranca-no-pix": boolean;
  "ativa-boleto-no-reexecutor-de-cobranca": boolean;
  "exibe-indique-e-ganhe-no-csat": boolean;
  "exibe-botoes-do-chatbot-zendesk-no-app": ShowChatbotZendeskButtons;
  "exibe-novo-fluxo-de-indicado": boolean;
  "exibe-cancelar-assinatura": boolean;
  "exibicao-itens-menu-pagina-meu-perfil": MenuPerfilConfig;
  "mostra-data-fim-fidelidade-antes-de-conversar-com-o-time": boolean;
  "exibe-feriados-no-calendario-de-todas-as-unidades": boolean;
  "novos-items-na-tela-de-agenda": ScheduleConfigPage;
  "deve-mostrar-novas-alteracoes-do-site": boolean;
  "permite-agendar-no-domingo": boolean;
  "ativa-cadastro-de-conta-recebedora": boolean;
  "habilita-historico-de-contatos-no-menu-do-assist-trial": boolean;
  "habilita-historico-de-contatos-no-menu-do-assist-essencial": boolean;
  "mostra-blog-no-app": boolean;
  "exibe-integracao-google-agenda": boolean;
  "exibe-sessao-proximos-passos-na-home": boolean;
  "exibe-opcao-de-agendar-onboarding-na-secao-proximos-passos-home": boolean;
  "ativa-agendamento-de-onboarding-online": boolean;
  "exibe-proximos-eventos-na-home": boolean;
  "exibe-nova-home": boolean;
  "bloqueia-uso-do-app-se-nao-possui-cartao-cadastrado": boolean;
  "redireciona-usuario-para-fluxos-se-pendente": boolean;
  "ativa-fluxo-de-agendamento-de-consultoria": boolean;
  "unidades-com-agendamento-de-consultoria-ativo": UnidadesConsultingConfig;
  "unidades-removidas-do-agendamento-de-onboarding": UnidadesRemovidasConfig;
  "ativa-transcricao-de-voz-na-consultoria": boolean;
  "nao-loga-erros-da-caf-no-discovery": boolean;
  "exibir-agendamento-recorrente": boolean;
  "exibir-tela-de-tarefas-assist": boolean;
  "estender-agendamento-recorrente": boolean;
  "data-de-inicio-do-rastreamento-de-presenca-no-onboarding": string;
  "exibe-configuracao-avancada-tipo-atendimento": boolean;
  "deve-validar-campos-obrigatorios-configuracao-avancada-tipo-de-atendimento": boolean;
  "deve-validar-campos-obrigatorios-usuario-forma-recebimento": boolean;
  "exibe-campos-convenio": boolean;
  "exibe-documentos-regulatorios-no-app": boolean;
  "exibe-documentos-regulatorios-de-declaracao-de-vinculos": boolean;
  /**
   * Renderiza o campo de categoria no formulário de criação de Período Garantido (KRA-516).
   *
   * **Um consumidor só: `ReservedScheduleForm`.** Slots, decorator, confirmação e `POST` leem
   * `reservedScheduleFormAtom.modality`, nunca esta flag — com ela desligada o submit grava a
   * pré-seleção, que é o valor que o loader resolveria hoje. Sem essa disciplina a flag vaza para
   * cinco arquivos e removê-la vira projeto.
   *
   * ## Fica **desligada em produção** até a T16 terminar
   *
   * O aceite do termo ainda é o gate de `/create`: o loader consulta
   * `TERMS_OF_USE_BY_MODALITY[defaultModality]` e redireciona para a rota `/terms-of-use` quando
   * falta aceite. Esse gate era exato enquanto a modalidade criada **era** a pré-seleção. Com o
   * campo, deixa de ser: quem aceitou só o termo da parceria pode escolher a convencional e criar
   * um PG cujo termo nunca assinou.
   *
   * Quem fecha isso é o **PR 3 da T16** — drawer de aceite no submit, chaveado na modalidade
   * *escolhida*, e a deleção da rota `/terms-of-use`. Até ele mergear, ligar esta flag em produção
   * abre essa lacuna. Ligar em desenvolvimento e homologação é o esperado; produção, não.
   *
   * **Remoção agendada** para a KRA-426 (T15), depois do rollout da KRA-440.
   */
  "exibe-selecao-de-modalidade-no-periodo-garantido": boolean;
  /**
   * Agenda vinculada ao Período Garantido (KRA-447): mostra, no fluxo de criação do PG, a opção de
   * abrir junto a agenda equivalente, e faz a confirmação enviar `abrirAgendaVinculada` e
   * `codUsuariosCompromissos` na action. Desligada, o fluxo e o request são os de hoje, byte a byte.
   *
   * Avaliada **por usuário** (targeting individual / regra por `codUsuario`), nunca por unidade —
   * o rollout é pessoa a pessoa. A flag no painel é a KRA-463; a chave tem que ser idêntica lá.
   *
   * **Remoção agendada** para depois do rollout da KRA-447.
   */
  "abre-agenda-vinculada-ao-criar-periodo-garantido": boolean;
  "exibe-configuracao-tipos-de-atendimento-na-primeira-agenda": boolean;
  "exibe-introducao-da-primeira-agenda": boolean;
  /**
   * Ajuste em lote de agendamentos na agenda do dia (KRAK-864 a KRAK-869): habilita selecionar
   * agendamentos de um mesmo dia e adiantar ou atrasar todos de uma vez (5 a 30 min).
   *
   * É o **gate na base da stack** — todo o fluxo (modo de seleção, barra de ação em lote,
   * confirmação e chamada a `remarcarAgendamentosEmBatch`) só aparece com a flag ligada.
   * Desligada, a agenda do dia se comporta exatamente como hoje.
   *
   * Backend: a revisão consulta a disponibilidade pela query `buscarReagendamentoEmBatch` e a
   * confirmação usa a mutation `remarcarAgendamentosEmBatch`.
   */
  "ajuste-em-lote-de-agendamentos": boolean;
  /**
   * Melhorias no tipo de atendimento (BLG-1123): troca o formulário de criação e edição pelo fluxo
   * em cards, com telas próprias de revisão, confirmação, tipo de sala e textos, e com o vínculo às
   * agendas na revisão (BLG-1119). Desligada, o membro vê o formulário de hoje, byte a byte.
   *
   * Avaliada **por usuário** (targeting individual / regra por `codUsuario`) — o rollout é pessoa a
   * pessoa, começando por contas de teste.
   *
   * Enquanto estiver desligada em produção, as duas versões convivem: o fluxo antigo mora nos
   * arquivos com sufixo `-legacy`, que são cópias congeladas e **não devem receber correções**.
   *
   * **Remoção agendada** para depois do rollout, junto com a deleção dos arquivos `-legacy`.
   */
  "ativa-melhorias-tipo-de-atendimento": boolean;
}

export const DefaultFeatureFlagsValue: FeatureFlags = {
  "antecedencia-minima-para-criacao-de-agendamento-com-preco-flexivel": 72,
  "ativa-melhorias-tipo-de-atendimento": false,
  "atualiza-mutation-receber-sala-agora-no-app-novo": false,
  "captura-feedback-sobre-horario-desejado": false,
  "configuracao-banner-csat": {
    ativo: false,
    exibirNovamenteEm: "00:00:00",
    exibirComentario: false,
  },
  "duracao-maxima-do-tipo-de-atendimento-em-minutos": 120,
  "duracao-tipo-atendimento-padrao-porto": 20,
  "exibe-botoes-de-indicacao-de-membros-para-pacientes": false,
  "exibe-marcar-paciente-como-atendido-na-sala-de-espera": false,
  "exibe-sugestao-de-duracao-para-tipo-de-atendimento-no-app": false,
  "exibir-drawer-de-feedback-ao-criar-um-agendamento": false,
  "exibir-indique-e-ganhe": false,
  "exibir-opcao-periodo-garantido-menu-app": false,
  "inclui-salas-flex-em-tipos-de-atendimento-que-aceitam-apenas-multi": false,
  "minimal-app-version": "0.0.0",
  "modalidade-periodo-garantido-por-usuario": 1,
  "permite-atendimentos-de-ate-15-minutos": false,
  "projeto-preco-flexivel": false,
  "deve-considerar-plano-mensal-como-permanencia-de-3-meses": false,
  "permitir-estender-horario-de-agendamento": false,
  "deve-exibir-etapa-de-redes-socias-no-fluxo-do-one-liv-no-app": false,
  "configura-rotas-que-aparece-barrinha-de-feedback": { routesWithUserFeedback: "none" },
  "envio-automatico-link-pagamento": false,
  "exibe-menu-visao-geral-relatorio-atendimento": false,
  "exibir-menu-livance-via": false,
  "exibir-relatorio-de-consulta": false,
  "ativa-fluxo-de-alterar-status-do-pagamento": false,
  "exibir-novo-componente-de-contratacao-do-livance-assist-pro": false,
  "exibe-banner-assist-no-menu": false,
  "altera-telefone-por-telefone-internacional": false,
  "habilita-digitacao-direta-de-data-de-nascimento": false,
  "habilita-item-de-relatorio-do-site-no-menu-do-assist": false,
  "remove-lista-de-recebimentos-de-gerenciar-pagamentos": false,
  "configuracao-condicoes-gerais-de-uso": {
    exibePagina: false,
    exibeVerDepois: false,
    dataCorteNovosMembros: "2025-01-01T00:00:00.000Z",
  },
  "exibe-botao-de-criar-pagamento-antecipado-no-drawer-de-agendamento-confirmado": false,
  "exibe-melhorias-do-relatorio-de-contatos": false,
  "exibe-nova-tela-de-paciente": false,
  "exibe-novo-relatorio-de-recuperacao-de-agendamentos": false,
  "exibe-campos-de-subespecialidades": false,
  "exibe-configuracoes-de-funcionalidades-assist": false,
  "exibe-termos-de-uso-link-pagamento": false,
  "mostra-comunidade-no-app": false,
  "mostra-aba-de-favoritos-na-comunidade-do-app": false,
  "exibe-busca-de-horarios-em-unidades-proximas": false,
  "exibe-botao-de-realizar-estorno": false,
  "exibe-filtro-no-historico-de-uso": false,
  "utiliza-v-5-da-pagar-me": false,
  "grupo-teste-ab-livance-pay-home": "A",
  "grupo-teste-ab-livance-assist-home": "C",
  "envio-de-pix-no-totem": false,
  "ativa-pull-to-refresh-na-home": false,
  "exibe-nova-experiencia-de-filtros": false,
  "membro-pode-contratar-pacote-servicos": false,
  "grupo-teste-ab-banner-assist-home": "OFF",
  "exibe-central-de-notificacoes": false,
  "habilita-historico-de-contatos-no-menu-do-assist": false,
  "exibe-valor-previsto-no-historico-de-contatos": false,
  "valor-do-gentileza-gera-gentileza": 180,
  "tempo-minimo-para-tipo-de-atendimento": 30,
  "exibe-fluxo-de-reexecutar-cobranca": false,
  "exibe-acoes-promocionais-do-membro-no-perfil-do-app": false,
  "textos-rota-info-home": {},
  "textos-confirmacao-rota-info-home": {},
  "gerenciar-titulo-e-descricao-do-gentileza-gera-gentileza": {
    homeBanner: {
      infoButton: "Indicar a Livance",
      infoDescription:
        "Ganhe **{{value}}** em créditos para cada colega que **abrir o consultório próprio** na Livance.",
      infoTitle: "Indique e ganhe",
    },
    cardBanner: {
      infoButton: "Indicar a Livance",
      infoDescription:
        "Ganhe **{{value}}** em créditos para cada colega que **abrir o consultório próprio** na Livance.",
      infoTitle: "",
    },
  },
  "exibir-banner-de-gentileza-gera-gentileza": { routesWithBannerReferral: "none" },
  "ativa-reexecucao-de-cobranca-no-pix": false,
  "ativa-boleto-no-reexecutor-de-cobranca": false,
  "exibe-indique-e-ganhe-no-csat": false,
  "exibe-botoes-do-chatbot-zendesk-no-app": {
    banner: false,
    header: false,
    modal: false,
    settings: false,
  },
  "exibe-novo-fluxo-de-indicado": false,
  "exibe-cancelar-assinatura": false,
  "exibicao-itens-menu-pagina-meu-perfil": {
    personalDataMenuItems: {
      showSection: false,
      personalData: false,
      professionalData: false,
      cars: false,
    },
    planAndAccessMenuItems: {
      showSection: true,
      yourPlan: true,
      accessInfo: false,
    },
  },
  "mostra-data-fim-fidelidade-antes-de-conversar-com-o-time": false,
  "exibe-feriados-no-calendario-de-todas-as-unidades": false,
  "novos-items-na-tela-de-agenda": {
    convenio: false,
    salas_preferenciais: false,
    preferencias_atendimento: false,
  },
  "deve-mostrar-novas-alteracoes-do-site": false,
  "permite-agendar-no-domingo": false,
  "ativa-cadastro-de-conta-recebedora": false,
  "habilita-historico-de-contatos-no-menu-do-assist-trial": false,
  "habilita-historico-de-contatos-no-menu-do-assist-essencial": false,
  "mostra-blog-no-app": false,
  "exibe-integracao-google-agenda": false,
  "exibe-sessao-proximos-passos-na-home": false,
  "exibe-opcao-de-agendar-onboarding-na-secao-proximos-passos-home": false,
  "ativa-agendamento-de-onboarding-online": false,
  "exibe-proximos-eventos-na-home": false,
  "exibe-nova-home": false,
  "bloqueia-uso-do-app-se-nao-possui-cartao-cadastrado": false,
  "redireciona-usuario-para-fluxos-se-pendente": false,
  "ativa-fluxo-de-agendamento-de-consultoria": false,
  "unidades-com-agendamento-de-consultoria-ativo": { unidades: [] },
  "unidades-removidas-do-agendamento-de-onboarding": {
    unidades: [1, 10, 17, 21, 23, 26, 27],
  },
  "ativa-transcricao-de-voz-na-consultoria": false,
  "nao-loga-erros-da-caf-no-discovery": false,
  "exibir-agendamento-recorrente": false,
  "exibir-tela-de-tarefas-assist": false,
  "estender-agendamento-recorrente": false,
  "data-de-inicio-do-rastreamento-de-presenca-no-onboarding": "2100-01-01",
  "exibe-configuracao-avancada-tipo-atendimento": false,
  "deve-validar-campos-obrigatorios-configuracao-avancada-tipo-de-atendimento": false,
  "deve-validar-campos-obrigatorios-usuario-forma-recebimento": false,
  "exibe-campos-convenio": false,
  "exibe-documentos-regulatorios-no-app": false,
  "exibe-documentos-regulatorios-de-declaracao-de-vinculos": false,
  "exibe-selecao-de-modalidade-no-periodo-garantido": false,
  "abre-agenda-vinculada-ao-criar-periodo-garantido": false,
  "exibe-configuracao-tipos-de-atendimento-na-primeira-agenda": false,
  "ajuste-em-lote-de-agendamentos": false,
  "exibe-introducao-da-primeira-agenda": false,
};

export interface UnidadesConsultingConfig {
  unidades: number[];
}

export interface UnidadesRemovidasConfig {
  unidades: number[];
}

export interface RoutesShowUserFeedbackConfig {
  routesWithUserFeedback: string[] | string;
}

export interface RoutesShowBannerReferralConfig {
  routesWithBannerReferral: string[] | string;
}

export interface TextsHomeInfoItemIdRouteFeatures {
  title: string;
  description: string;
}

export type TextsHomeInfoItemIdRoute =
  | Record<
      string,
      {
        title: string;
        pageTitle: string;
        description?: string | null;
        buttonText: string;
        pricingText: string;
        iconName: string;
        features: TextsHomeInfoItemIdRouteFeatures[];
        eventName: string;
        eventProps: Record<string, string>;
      }
    >
  | undefined;

export type TextsConfirmationInfoItemIdRoute =
  | Record<
      string,
      {
        title: string;
        pageTitle: string;
        description?: string | null;
        iconName: string;
        buttonText: string;
        eventName: string;
        eventProps: Record<string, string>;
      }
    >
  | undefined;

export interface MenuPerfilConfig {
  personalDataMenuItems: {
    showSection: boolean;
    personalData: boolean;
    professionalData: boolean;
    cars: boolean;
  };
  planAndAccessMenuItems: {
    showSection: boolean;
    yourPlan: boolean;
    accessInfo: boolean;
  };
}

export interface ScheduleConfigPage {
  convenio: boolean;
  preferencias_atendimento: boolean;
  salas_preferenciais: boolean;
}

export type ShowChatbotZendeskButtons =
  | {
      header: boolean;
      banner: boolean;
      modal: boolean;
      settings: boolean;
    }
  | undefined;

export interface TextsDescriptionReferral {
  infoTitle?: string;
  infoDescription?: string;
  infoButton?: string;
}

export interface InfoVariantBannerReferral {
  homeBanner?: TextsDescriptionReferral;
  cardBanner?: TextsDescriptionReferral;
}

export interface CsatConfig {
  exibirNovamenteEm: string;
  exibirComentario: boolean;
  ativo: boolean;
}

export interface GeneralTermsConfig {
  exibePagina: boolean;
  exibeVerDepois: boolean;
  dataCorteNovosMembros: string;
}

const anonymousUserContext: LDContext = {
  kind: "user",
  anonymous: true,
};

const defaultContext: LDMultiKindContext = {
  kind: "multi",
  user: anonymousUserContext,
};

export const withDefaultLDProvider = (app: React.ComponentType): React.ComponentType => {
  return withLDProvider({
    clientSideID: VITE_LAUNCHDARKLY_SDK_KEY,
    context: defaultContext,
    flags: DefaultFeatureFlagsValue,
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  })(app);
};

export class LaunchDarklyClient {
  constructor(ldClient: LDClient) {
    this.instance = ldClient;
  }

  private instance: LDClient;

  private resolvedIp: string | undefined;

  private get currentContext(): LDMultiKindContext {
    return this.instance.getContext() as LDMultiKindContext;
  }

  public getFlagValue<T>(key: keyof FeatureFlags, defaultValue: T): T {
    const flagValue = this.instance.variation(key, defaultValue) as T;
    return flagValue ?? defaultValue;
  }

  public userContextAlreadyAdded(): boolean {
    return (
      (this.currentContext.user as LDContextCommon).key !== undefined &&
      !(this.currentContext.user as LDContextCommon).anonymous
    );
  }

  public async resetContext(): Promise<void> {
    await this.identify(defaultContext);
  }

  public async addContext(context: LDSingleKindContext): Promise<void> {
    const newContext = this.mergeContext(this.currentContext, context);

    if (newContext) {
      await this.identify(newContext);
    }
  }

  /**
   * Resolves the user's IP (best-effort) and adds it to the LaunchDarkly user
   * context so IP-based flag targeting works. Safe to call on every navigation:
   * the IP is fetched at most once and re-applying an unchanged value is a no-op.
   * Network/parse failures degrade gracefully and never throw.
   */
  public async ensureUserIp(): Promise<void> {
    if (this.resolvedIp === undefined) {
      try {
        this.resolvedIp = (await getIP()).trim();
      } catch {
        // Offline/timeout/CORS: leave unresolved so a later call can retry.
        return;
      }
    }

    if (!this.resolvedIp) {
      return;
    }

    await this.setUserContextIp(this.resolvedIp);
  }

  private async setUserContextIp(ip: string): Promise<void> {
    const existingUser = this.currentContext.user;
    // Fall back to the anonymous context (which carries kind: "user") if the
    // user context is missing or is the top-level "multi" marker, so identify
    // never receives an incomplete user context.
    const currentUser =
      typeof existingUser === "object" ? existingUser : anonymousUserContext;

    if (currentUser.ip === ip) {
      return;
    }

    await this.identify({
      ...this.currentContext,
      user: { ...currentUser, ip },
    });
  }

  public async addContexts(contexts: LDSingleKindContext[]): Promise<void> {
    let changed: boolean | LDMultiKindContext = false;
    let currentContext = this.currentContext;
    for (const context of contexts) {
      changed = this.mergeContext(currentContext, context) || changed;
      currentContext = changed ? changed : currentContext;
    }
    if (changed) {
      await this.identify(changed);
    }
  }

  private mergeContext(
    baseContext: LDMultiKindContext,
    context: LDSingleKindContext,
  ): LDMultiKindContext | false {
    const current = baseContext[context.kind] as LDSingleKindContext | undefined;
    if (!current || current.key !== context.key) {
      return { ...baseContext, [context.kind]: context };
    }
    return false;
  }

  private async identify(context: LDMultiKindContext): Promise<LDClient> {
    try {
      await this.instance.identify(context);

      await this.instance.waitForInitialization();
    } catch (error) {
      logError(error);
    }

    return this.instance;
  }

  public getShowCreatedAppointmentFeedbackDrawer(): boolean {
    return this.getFlagValue<boolean>(
      "exibir-drawer-de-feedback-ao-criar-um-agendamento",
      DefaultFeatureFlagsValue["exibir-drawer-de-feedback-ao-criar-um-agendamento"],
    );
  }

  public getShowFeedbackCaptureDesiredTime(): boolean {
    return this.getFlagValue<boolean>(
      "captura-feedback-sobre-horario-desejado",
      DefaultFeatureFlagsValue["captura-feedback-sobre-horario-desejado"],
    );
  }

  public getShowMembersRecommendationForPatientsButtons(): boolean {
    return this.getFlagValue<boolean>(
      "exibe-botoes-de-indicacao-de-membros-para-pacientes",
      DefaultFeatureFlagsValue["exibe-botoes-de-indicacao-de-membros-para-pacientes"],
    );
  }

  public getShowAppointmentDurationSuggestion(): boolean {
    return this.getFlagValue<boolean>(
      "exibe-sugestao-de-duracao-para-tipo-de-atendimento-no-app",
      DefaultFeatureFlagsValue[
        "exibe-sugestao-de-duracao-para-tipo-de-atendimento-no-app"
      ],
    );
  }
  public getAllow15MinutesAppointment(): boolean {
    return this.getFlagValue<boolean>(
      "permite-atendimentos-de-ate-15-minutos",
      DefaultFeatureFlagsValue["permite-atendimentos-de-ate-15-minutos"],
    );
  }

  public getReservedScheduleTypeByUser(): number {
    return this.getFlagValue<number>(
      "modalidade-periodo-garantido-por-usuario",
      DefaultFeatureFlagsValue["modalidade-periodo-garantido-por-usuario"],
    );
  }

  public getAppointmentTypeMaxDurationInMinutes(): number {
    return this.getFlagValue<number>(
      "duracao-maxima-do-tipo-de-atendimento-em-minutos",
      DefaultFeatureFlagsValue["duracao-maxima-do-tipo-de-atendimento-em-minutos"],
    );
  }

  public getPortoAppointmentTypeDuration(): number {
    return this.getFlagValue<number>(
      "duracao-tipo-atendimento-padrao-porto",
      DefaultFeatureFlagsValue["duracao-tipo-atendimento-padrao-porto"],
    );
  }

  public async getFlexiblePriceAvailability(codUnidade: number): Promise<boolean> {
    await this.addContext({ kind: "office", key: `livance-${codUnidade}` });
    return this.getFlagValue<boolean>(
      "projeto-preco-flexivel",
      DefaultFeatureFlagsValue["projeto-preco-flexivel"],
    );
  }

  public getMinimumAntecedenceForFlexiblePriceAppointmentCreation(): number {
    return this.getFlagValue<number>(
      "antecedencia-minima-para-criacao-de-agendamento-com-preco-flexivel",
      DefaultFeatureFlagsValue[
        "antecedencia-minima-para-criacao-de-agendamento-com-preco-flexivel"
      ],
    );
  }

  public getShowTreatedAppointmentInWaitingRoom(): boolean {
    return this.getFlagValue<boolean>(
      "exibe-marcar-paciente-como-atendido-na-sala-de-espera",
      DefaultFeatureFlagsValue["exibe-marcar-paciente-como-atendido-na-sala-de-espera"],
    );
  }

  public getUseNewMutationReceiveRoomNow(): boolean {
    return this.getFlagValue<boolean>(
      "atualiza-mutation-receber-sala-agora-no-app-novo",
      DefaultFeatureFlagsValue["atualiza-mutation-receber-sala-agora-no-app-novo"],
    );
  }

  public getMinimalAppVersion(): Version {
    const flagValue: string = this.getFlagValue<string>(
      "minimal-app-version",
      DefaultFeatureFlagsValue["minimal-app-version"],
    );
    return new Version(flagValue);
  }

  public getIncludesFlexRoomInMultiAppointment(): boolean {
    return this.getFlagValue<boolean>(
      "inclui-salas-flex-em-tipos-de-atendimento-que-aceitam-apenas-multi",
      DefaultFeatureFlagsValue[
        "inclui-salas-flex-em-tipos-de-atendimento-que-aceitam-apenas-multi"
      ],
    );
  }

  public getGeneralTermsConfiguration(): GeneralTermsConfig {
    return this.getFlagValue<GeneralTermsConfig>(
      "configuracao-condicoes-gerais-de-uso",
      DefaultFeatureFlagsValue["configuracao-condicoes-gerais-de-uso"],
    );
  }
}
